 

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

const ZOOM_DEFAULT = 600;
const ZOOM_MIN = 100;
const ZOOM_MAX = 1500;
const ZOOM_STEP = 50;
const WHEEL_THROTTLE_MS = 80;

const PackageDetailsImageDialog = ({
  open,
  onClose,
  imageUrl,
  onDownload
}) => {
  const [zoomLevel, setZoomLevel] = useState(ZOOM_DEFAULT);
  const [rotationDeg, setRotationDeg] = useState(0);
  const containerRef = useRef(null);
  const lastWheelTime = useRef(0);

  useEffect(() => {
    if (open) {
      setZoomLevel(ZOOM_DEFAULT);
      setRotationDeg(0);
    }
  }, [open]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !open) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const now = Date.now();
      if (now - lastWheelTime.current < WHEEL_THROTTLE_MS) return;
      lastWheelTime.current = now;
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      setZoomLevel((prev) => Math.max(ZOOM_MIN, Math.min(prev + delta, ZOOM_MAX)));
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [open]);

  const handleRotate = () => {
    setRotationDeg((prev) => (prev + 90) % 360);
    setZoomLevel(ZOOM_DEFAULT);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent
        ref={containerRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#000',
          p: 2,
          height: '70vh',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          <img
            src={imageUrl}
            alt="Full size"
            style={{
              height: `${zoomLevel}px`,
              width: 'auto',
              maxWidth: 'none',
              objectFit: 'contain',
              display: 'block',
              transform: `rotate(${rotationDeg}deg)`,
              transformOrigin: 'center',
              transition: 'transform 0.2s ease'
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          pb: 2,
          backgroundColor: '#1E2235'
        }}
      >
        <Box>
          <Tooltip title="Zoom In">
            <IconButton
              onClick={() =>
                setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, ZOOM_MAX))
              }
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton
              onClick={() =>
                setZoomLevel((prev) => Math.max(ZOOM_MIN, prev - ZOOM_STEP))
              }
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rotate">
            <IconButton onClick={handleRotate}>
              <RotateRightIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton onClick={onDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title="Close">
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default PackageDetailsImageDialog;
